"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ComponentProps } from "react";
import { memo } from "react";

export type ConfirmationProps = ComponentProps<"div">;

export const Confirmation = memo(
  ({ className, ...props }: ConfirmationProps) => (
    <div
      data-slot="confirmation"
      className={cn(
        "rounded-xl border bg-card text-card-foreground shadow-sm px-3.5 py-5",
        className
      )}
      {...props}
    />
  )
);

export type ConfirmationTitleProps = ComponentProps<"div">;

export const ConfirmationTitle = memo(
  ({ className, ...props }: ConfirmationTitleProps) => (
    <div
      data-slot="confirmation-title"
      className={cn("font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  )
);

export type ConfirmationDescriptionProps = ComponentProps<"div">;

export const ConfirmationDescription = memo(
  ({ className, ...props }: ConfirmationDescriptionProps) => (
    <div
      data-slot="confirmation-description"
      className={cn("text-sm text-muted-foreground mt-1.5", className)}
      {...props}
    />
  )
);

export type ConfirmationActionsProps = ComponentProps<"div">;

export const ConfirmationActions = memo(
  ({ className, ...props }: ConfirmationActionsProps) => (
    <div
      data-slot="confirmation-actions"
      className={cn("flex items-center gap-2 mt-4", className)}
      {...props}
    />
  )
);

export type ConfirmationActionProps = ComponentProps<typeof Button>;

export const ConfirmationAction = memo(
  ({ className, ...props }: ConfirmationActionProps) => (
    <Button
      data-slot="confirmation-action"
      size="sm"
      className={className}
      {...props}
    />
  )
);

export type ConfirmationAcceptedProps = ComponentProps<"div">;

export const ConfirmationAccepted = memo(
  ({ className, ...props }: ConfirmationAcceptedProps) => (
    <div
      data-slot="confirmation-accepted"
      className={cn(
        "rounded-xl border bg-card text-card-foreground shadow-sm p-6 flex items-center gap-2 text-sm text-muted-foreground",
        className
      )}
      {...props}
    />
  )
);

Confirmation.displayName = "Confirmation";
ConfirmationTitle.displayName = "ConfirmationTitle";
ConfirmationDescription.displayName = "ConfirmationDescription";
ConfirmationActions.displayName = "ConfirmationActions";
ConfirmationAction.displayName = "ConfirmationAction";
ConfirmationAccepted.displayName = "ConfirmationAccepted";
